/**
 * Confirmation and toasts.
 *
 * EVERY mutating action in this panel goes through `confirm()`. That is not a
 * politeness — an operator approving a withdrawal is releasing real money, and
 * a deposit confirmation credits a balance that cannot be un-credited without a
 * compensating ledger entry. The dialog restates the exact figures, the player
 * and the destination, so the last thing seen before the click is what is
 * actually about to happen rather than what the operator remembers clicking on.
 *
 * A destructive action can additionally demand a typed reason (rejections) or a
 * typed confirmation phrase (anything that cannot be walked back).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconAlert, IconCheck, IconLock, IconShield } from './icons.tsx';

export interface ConfirmFact {
  label: string;
  value: ReactNode;
}

export interface ConfirmRequest {
  title: string;
  body?: ReactNode;
  facts?: ConfirmFact[];
  confirmLabel: string;
  intent?: 'default' | 'danger';
  /** A note the operator must (or may) attach. Stored on the reviewed row. */
  note?: { label: string; required: boolean; hint?: string; placeholder?: string };
  /** Text that must be typed exactly. Reserved for the irreversible. */
  challenge?: { phrase: string; hint: string };
}

export type ConfirmResult = { confirmed: false } | { confirmed: true; note: string };

type ConfirmFn = (request: ConfirmRequest) => Promise<ConfirmResult>;

const ConfirmContext = createContext<ConfirmFn | null>(null);
const ToastContext = createContext<((message: string, tone?: 'ok' | 'error') => void) | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm used outside FeedbackProvider');
  return fn;
}

export function useToast(): (message: string, tone?: 'ok' | 'error') => void {
  const fn = useContext(ToastContext);
  if (!fn) throw new Error('useToast used outside FeedbackProvider');
  return fn;
}

interface Toast {
  id: number;
  message: string;
  tone: 'ok' | 'error';
}

export function FeedbackProvider({ children }: { children: ReactNode }): ReactNode {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const resolver = useRef<((result: ConfirmResult) => void) | null>(null);
  const nextToastId = useRef(1);

  const confirm = useCallback<ConfirmFn>((next) => {
    return new Promise<ConfirmResult>((resolve) => {
      resolver.current = resolve;
      setRequest(next);
    });
  }, []);

  const finish = useCallback((result: ConfirmResult) => {
    setRequest(null);
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  const toast = useCallback((message: string, tone: 'ok' | 'error' = 'ok') => {
    const id = nextToastId.current;
    nextToastId.current += 1;
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 6000);
  }, []);

  const confirmValue = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={confirmValue}>
      <ToastContext.Provider value={toast}>
        {children}
        {request ? <ConfirmDialog request={request} onFinish={finish} /> : null}
        <div className="toast-layer">
          {toasts.map((t) => (
            <div key={t.id} className={`toast${t.tone === 'error' ? ' toast--error' : ''}`}>
              {t.tone === 'error' ? <IconAlert /> : <IconCheck />}
              <span>{t.message}</span>
            </div>
          ))}
        </div>
      </ToastContext.Provider>
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  request,
  onFinish,
}: {
  request: ConfirmRequest;
  onFinish: (result: ConfirmResult) => void;
}): ReactNode {
  const [note, setNote] = useState('');
  const [challenge, setChallenge] = useState('');
  const [touched, setTouched] = useState(false);

  const noteMissing = Boolean(request.note?.required) && note.trim().length === 0;
  const challengeMissing =
    request.challenge !== undefined && challenge.trim() !== request.challenge.phrase;
  const blocked = noteMissing || challengeMissing;

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onFinish({ confirmed: false });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFinish]);

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-label={request.title}>
      <div className="modal">
        <div className="modal__head">
          <span
            className={`modal__icon${request.intent === 'danger' ? ' modal__icon--danger' : ''}`}
          >
            {request.intent === 'danger' ? (
              <IconAlert size="lg" />
            ) : request.challenge ? (
              <IconLock size="lg" />
            ) : (
              <IconShield size="lg" />
            )}
          </span>
          <h2 className="modal__title">{request.title}</h2>
        </div>
        {request.body ? <div className="modal__body">{request.body}</div> : null}

        {request.facts && request.facts.length > 0 ? (
          <div className="modal__facts">
            {request.facts.map((fact) => (
              <div key={fact.label} style={{ display: 'contents' }}>
                <span className="modal__fact-label">{fact.label}</span>
                <span className="modal__fact-value">{fact.value}</span>
              </div>
            ))}
          </div>
        ) : null}

        {request.note ? (
          <label className="field">
            <span className="field__label">
              {request.note.label}
              {request.note.required ? ' (required)' : ' (optional)'}
            </span>
            <textarea
              className="textarea"
              value={note}
              autoFocus
              placeholder={request.note.placeholder ?? ''}
              onChange={(event) => setNote(event.target.value)}
              onBlur={() => setTouched(true)}
            />
            {request.note.hint ? <span className="field__hint">{request.note.hint}</span> : null}
            {touched && noteMissing ? (
              <span className="field__error">A reason is required — the player is told it.</span>
            ) : null}
          </label>
        ) : null}

        {request.challenge ? (
          <label className="field">
            <span className="field__label">Type “{request.challenge.phrase}” to continue</span>
            <input
              className="input"
              value={challenge}
              onChange={(event) => setChallenge(event.target.value)}
            />
            <span className="field__hint">{request.challenge.hint}</span>
          </label>
        ) : null}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => onFinish({ confirmed: false })}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${request.intent === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            disabled={blocked}
            onClick={() => onFinish({ confirmed: true, note: note.trim() })}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
